import { STORYBOARD_TYPES } from "constants/storyboardType";
import { AUDIO, DOCUMENT, VIDEO, DEFAULT } from "constants/widgetAssetTypes";
import _ from "lodash";

export default class FileService {
  static formatSize(bytes) {
    if (bytes >= 1000000000) {
      return (bytes / 1000000000).toFixed(2) + " GB";
    }
    if (bytes >= 1000000) {
      return (bytes / 1000000).toFixed(2) + " MB";
    }
    return (bytes / 1000).toFixed(2) + " KB";
  }

  static formatDuration(timeSpent) {
    let timeSpentValue = `${FileService.padNumber(timeSpent, 1)} Seconds`;

    if (timeSpent >= 60) {
      var minutes = Math.round(timeSpent / 60);
      timeSpentValue = `${FileService.padNumber(minutes, 1)} Minute${minutes == 1 ? "" : "s"
        }`;
    }

    return timeSpentValue;
  }

  static formatLengthDuration(durationInSeconds) {
    const hours = Math.floor(Math.round(durationInSeconds) / 3600);
    const minutes = Math.floor((Math.round(durationInSeconds) % 3600) / 60);
    const seconds = Math.round(durationInSeconds)  % 60;
  
    // Add leading zeros for single-digit hours, minutes, and seconds
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
  
    if (hours > 0) {
      return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    } else {
      return `${formattedMinutes}:${formattedSeconds}`;
    }
  }

  static padNumber(number = 0, pad) {
    number = Math.ceil(number);

    const length = pad - String(number).length + 1;
    let padding = "";
    if (length > 0) {
      padding = Array(pad - String(number).length + 1).join("0");
    }
    return padding + number;
  }

  static fileType(metadata) {
    if(!metadata || !metadata.content_type) {
      return;
    }
    
    switch (metadata.content_type) {
      case "application/pdf":
        return "PDF";
      case "video/mp4":
      case "video/x-ms-wmv":
      case "video/avi":
      case "video/x-matroska":
      case "video/quicktime":
      case "video/mpeg":
      case "video/x-flv":
      case "video/3gpp":
      case "video/webm":
        return "VIDEO";
      case "image/gif":
      case "image/tiff":
      case "image/png":
      case "image/bmp":
      case "image/jpeg":
      case "image/svg+xml":
        return "IMAGE";
      case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      case "application/vnd.openxmlformats-officedocument.presentationml.template":
      case "application/vnd.openxmlformats-officedocument.presentationml.slideshow":
      case "application/vnd.ms-powerpoint.addin.macroEnabled.12":
      case "application/vnd.ms-powerpoint.presentation.macroEnabled.12":
      case "application/vnd.ms-powerpoint.template.macroEnabled.12":
      case "application/vnd.ms-powerpoint.slideshow.macroEnabled.12":
        return "PPTX";
      case "application/vnd.ms-powerpoint":
      case "application/vnd.google-apps.presentation":
        return "PPT";
      case "application/msword":
      case "application/vnd.google-apps.document":
        return "DOC";
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml.template":
      case "application/vnd.ms-word.template.macroEnabled.12":
        return "DOCX";
      case "application/vnd.ms-excel":
        return "XLS"
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      case "application/vnd.google-apps.spreadsheet":
      case "application/vnd.openxmlformats-officedocument.spreadsheetml.template":
      case "application/vnd.ms-excel.sheet.macroEnabled.12":
      case "application/vnd.ms-excel.template.macroEnabled.12":
      case "application/vnd.ms-excel.addin.macroEnabled.12":
      case "application/vnd.ms-excel.sheet.binary.macroEnabled.12":
        return "XLSX"
      case "application/url":
        if (metadata.external_video_provider) {
          if (metadata.external_video_provider == "youtube") {
            return "YOUTUBE";
          } else if (metadata.external_video_provider == "vimeo") {
            return "VIMEO";
          } else if (metadata.external_video_provider == "wistia") {
            return "WISTIA";
          }
          else if (metadata.external_video_provider == "vidyard") {
            return "VIDYARD";
          }
        }
        return "LINK";
      case 'audio/mp3':
      case 'audio/wav':
      case 'audio/wma':
      case 'audio/aac':  
      case 'audio/mpeg':
      case 'audio/aiff':
      case 'audio/flac':
      case 'audio/x-aiff':
        return 'AUDIO';
      case 'application/zip':
      case 'application/x-zip-compressed':
         if (metadata.scorm){
          return "SCORM"
        }
        return 'ZIP';
      default:
        return (_.get(metadata, 'extension') || "").toUpperCase();
    }
  }

  static fileIcon(metaData) {
    const contentType = this.fileType(metaData);
    switch (contentType) {
      case 'PDF':
        return 'cleverstory-pdf';
      case 'YOUTUBE':
        return 'cleverstory-YouTube-2';
      case 'VIMEO':
        return 'cleverstory-Vimeo';
      case 'VIDEO':
      case 'WISTIA':
      case 'VIDYARD':
        return 'cleverstory-video';
      case 'IMAGE':
        return 'cleverstory-background';
      case 'PPTX':
        return 'cleverstory-pptx';
      case 'PPT':
        return 'cleverstory-ppt';
      case 'DOCX':
        return 'cleverstory-docx';
      case 'DOC':
      case 'XLS':
      case 'XLSX':
        return 'cleverstory-doc';
      case 'LINK':
        return 'cleverstory-link';
      case 'AUDIO':
        return 'cleverstory-audio';
      case 'SCORM':
        return 'cleverstory-content';
      default:
        return 'cleverstory-broken';
    }
  }

  static fileMetaData(metaData) {
    const contentType = this.fileType(metaData);
    switch (contentType) {
      case 'PDF':
        if(!metaData.pages){
          return null
        }
        return metaData.pages < 2 ? `${metaData.pages} Page`: `${metaData.pages} Pages`;
      case 'YOUTUBE':
      case 'VIDEO':
      case 'VIMEO':
      case 'WISTIA':
      case 'VIDYARD':
        return this.formatLengthDuration(metaData.video_duration);
      case 'IMAGE':
        return this.formatSize(metaData.content_length);
      case 'PPTX':
      case 'PPT':
        if(!metaData.pages){
          return null
        }
        return metaData.pages < 2 ? `${metaData.pages} Page`: `${metaData.pages} Pages`;
      case 'DOC':
      case 'DOCX':
        if(!metaData.pages){
          return null
        }
        return metaData.pages < 2 ? `${metaData.pages} Page`: `${metaData.pages} Pages`;
      case 'XLS':
      case 'XLSX':
        return this.formatSize(metaData.content_length);
      case 'LINK':
        return null;
      case 'AUDIO':
        return this.formatSize(metaData.content_length);
      default:
        return null;
    }
  }

  static fileContentType(metaData) {
    const contentType = this.fileType(metaData);
    const source_type = _.get(metaData, 'source_type')

    switch (contentType) {
      case 'PDF':
      case 'PPTX':
      case 'PPT':
      case 'DOC':
      case 'DOCX':
        return DOCUMENT;
      case 'YOUTUBE':
      case 'VIDEO':
      case 'VIMEO':
      case 'WISTIA':
      case 'VIDYARD':
        return VIDEO;
      case 'AUDIO':
        return AUDIO;
      case 'LINK':
        if (source_type === 'PDF') {
          return DOCUMENT;
        }
      default:
        return DEFAULT;
    }
  }

  static storyboardIcon(type) {
    switch (type) {
      case 'LANDING_PAGE':
        return 'cleverstory-landing-page';
      case STORYBOARD_TYPES.INTERACTIVE_CONTENT:
      case STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2:
        return 'cleverstory-Interactive';
      case 'RESOURCE_CENTER':
        return 'cleverstory-resource-center';
      case 'SPATIAL':
        return 'cleverstory-Interactive';
      case STORYBOARD_TYPES.FLIP_BOOK:
        return 'cleverstory-Interactive';
    }
  }

}


